// Client-side ZIP bundler for a case. Packs:
//   - case.json: case metadata
//   - records.json: all records as structured JSON
//   - records.csv:  CSV flattening of the same records
//   - attachments/NNN-<filename>: each record's R2 blob, indexed by order
//
// JSZip is ~40KB gzip, lazily imported so it doesn't bloat the main bundle.

import type { Case, CaseRecord } from "../types";
import { fileUrl } from "../api";

interface Bundle {
  case: Case;
  records: CaseRecord[];
}

function csvEscape(v: unknown): string {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function pad(n: number, len: number): string {
  return String(n).padStart(len, "0");
}

function safeFilename(name: string, fallback: string): string {
  const clean = name.replace(/[/\\?%*:|"<>]/g, "_").trim();
  return clean || fallback;
}

interface AttachmentFetch {
  buffer: ArrayBuffer | null;
  error: string | null;
}

async function fetchAttachment(
  url: string,
  authHeaders: Record<string, string>,
): Promise<AttachmentFetch> {
  try {
    const res = await fetch(url, {
      credentials: "include",
      headers: authHeaders,
    });
    if (!res.ok) {
      return { buffer: null, error: `HTTP ${res.status} ${res.statusText}` };
    }
    return { buffer: await res.arrayBuffer(), error: null };
  } catch (err) {
    return { buffer: null, error: (err as Error).message || "network error" };
  }
}

export async function exportCaseAsZip(
  { case: caseData, records }: Bundle,
  authHeaders: Record<string, string>,
): Promise<Blob> {
  const { default: JSZip } = await import("jszip");
  const zip = new JSZip();

  zip.file("case.json", JSON.stringify(caseData, null, 2));
  zip.file("records.json", JSON.stringify(records, null, 2));

  const csvHeaders = [
    "Date",
    "Added",
    "Direction",
    "Source type",
    "From",
    "To",
    "Via",
    "Subject",
    "Body",
    "File name",
    "File type",
    "Attachment path",
  ];
  const csvLines: string[] = [csvHeaders.map(csvEscape).join(",")];

  const indexWidth = Math.max(3, String(records.length).length);
  const attachmentsDir = zip.folder("attachments");
  // Collect per-file failures so we can append a manifest — otherwise
  // a partially-missing archive looks identical to a complete one and
  // the user has no way to know what's missing.
  const missing: Array<{ record: number; filename: string; reason: string }> =
    [];

  for (let i = 0; i < records.length; i++) {
    const r = records[i]!;
    let attachmentPath = "";

    if (r.file_r2_key || r.image_r2_key) {
      const key = r.file_r2_key ?? r.image_r2_key!;
      const url = fileUrl(caseData.id, key);
      const base = safeFilename(r.file_name ?? "attachment", "attachment");
      const { buffer, error } = await fetchAttachment(url, authHeaders);
      if (buffer) {
        const name = `${pad(i + 1, indexWidth)}-${base}`;
        attachmentsDir?.file(name, buffer);
        attachmentPath = `attachments/${name}`;
      } else {
        missing.push({
          record: i + 1,
          filename: r.file_name ?? key,
          reason: error ?? "unknown",
        });
      }
    }

    csvLines.push(
      [
        r.record_date,
        r.logged_at,
        r.direction,
        r.source_type,
        r.from_addr,
        r.to_addr,
        r.forwarded_via,
        r.subject,
        r.body_text,
        r.file_name,
        r.file_mime_type,
        attachmentPath,
      ]
        .map(csvEscape)
        .join(","),
    );
  }

  zip.file("records.csv", csvLines.join("\r\n"));

  if (missing.length > 0) {
    const lines = [
      "CaseThread export — missing attachments",
      "",
      `${missing.length} attachment${missing.length === 1 ? "" : "s"} could not be downloaded when this archive was built.`,
      "The corresponding records.csv / records.json entries are present, but the binary files are not in the attachments/ folder.",
      "",
      "Record # | Filename | Reason",
      "---------|----------|-------",
      ...missing.map(
        (m) => `${String(m.record).padStart(4)}    | ${m.filename} | ${m.reason}`,
      ),
    ];
    zip.file("_MISSING_FILES.txt", lines.join("\n"));
  }

  return zip.generateAsync({ type: "blob", compression: "DEFLATE" });
}
